const mobileQuery = window.matchMedia('(max-width: 749px)');
const accessibilityStorageKey = 'suncoAccessibilitySettings';
const accessibilityClasses = {
  text: 'sunco-a11y-large-text',
  contrast: 'sunco-a11y-high-contrast',
  links: 'sunco-a11y-underline-links',
  motion: 'sunco-a11y-reduce-motion',
};
const footerModalTriggers = new WeakMap();
const accessibilityTriggerSources = new WeakMap();

function getModalFocusables(modal) {
  const panel = modal?.querySelector('.sunco-footer-modal__content');
  if (!panel) return [];

  return [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.hidden && element.getClientRects().length > 0);
}

function trapModalFocus(event, modal) {
  const focusable = getModalFocusables(modal);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (!modal.querySelector('.sunco-footer-modal__content')?.contains(active)) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  } else if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function closeModal(modal) {
  if (!modal) return;
  modal.classList.remove('is-active');
  modal.removeAttribute('data-open');
  modal.setAttribute('aria-hidden', 'true');
  const trigger = footerModalTriggers.get(modal);
  if (trigger instanceof HTMLElement && trigger.isConnected) {
    trigger.focus({ preventScroll: true });
  }
  footerModalTriggers.delete(modal);
}

function openModal(modal, trigger) {
  if (!modal) return;
  if (trigger instanceof HTMLElement) footerModalTriggers.set(modal, trigger);
  modal.classList.add('is-active');
  modal.setAttribute('data-open', 'true');
  modal.setAttribute('aria-hidden', 'false');
  const closeButton = modal.querySelector('[data-sunco-footer-modal-close]');
  closeButton?.focus({ preventScroll: true });
}

function initFooterAccordions(footer) {
  footer.querySelectorAll('[data-sunco-footer-toggle]').forEach((toggle) => {
    const menu = toggle.closest('.sunco-footer-menu');

    toggle.addEventListener('click', () => {
      if (!mobileQuery.matches || !menu) return;

      const isOpen = menu.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });

  const syncAccordionState = () => {
    footer.querySelectorAll('.sunco-footer-menu').forEach((menu) => {
      const toggle = menu.querySelector('[data-sunco-footer-toggle]');
      if (!toggle) return;

      if (mobileQuery.matches) {
        menu.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        menu.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  };

  syncAccordionState();
  mobileQuery.addEventListener('change', syncAccordionState);
}

function initFooterModals(footer) {
  footer.querySelectorAll('[data-sunco-footer-modal-close]').forEach((trigger) => {
    trigger.addEventListener('click', () => closeModal(trigger.closest('.sunco-footer-modal')));
  });

  footer.querySelectorAll('[data-sunco-footer-modal][data-open="true"]').forEach((modal) => {
    openModal(modal);
  });

  footer.querySelectorAll('[data-sunco-footer-modal]:not([data-open="true"])').forEach((modal) => {
    modal.setAttribute('aria-hidden', 'true');
  });

  const privacyModal = footer.querySelector('[data-sunco-privacy-modal]');
  footer.querySelectorAll('.dontdoIt').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      openModal(privacyModal, trigger);
    });
  });

  document.addEventListener('keydown', (event) => {
    footer.querySelectorAll('.sunco-footer-modal.is-active').forEach((modal) => {
      if (event.key === 'Escape') closeModal(modal);
      else if (event.key === 'Tab') trapModalFocus(event, modal);
    });
  });
}

function initEqualWeb(footer) {
  const sitekey = footer.dataset.suncoEqualwebSitekey;
  if (!sitekey) return;

  const position = footer.dataset.suncoEqualwebPosition || 'left';

  window.interdeal = window.interdeal || {
    sitekey,
    Position: position,
    domains: {
      js: 'https://cdn.equalweb.com/',
      acc: 'https://access.equalweb.com/',
    },
    Menulang: 'EN',
    btnStyle: {
      vPosition: ['80%', '20%'],
      scale: ['0.5', '0.5'],
      color: {
        main: '#2e2e2e',
        second: '#ffffff',
      },
      icon: {
        outline: false,
        type: 2,
        shape: 'circle',
      },
    },
  };

  const trigger = footer.querySelector('.sunco-footer__accessibility');
  trigger?.addEventListener('click', (event) => {
    event.preventDefault();
  });

  if (document.querySelector('script[data-sunco-equalweb-script]')) return;

  const script = document.createElement('script');
  script.src = `${window.interdeal.domains.js}core/5.0.13/accessibility.js`;
  script.defer = true;
  script.integrity = 'sha512-pk3CeR0KGJu+GfK2x2ybTSZ1o1qfua6XW2PRAxMWOhC85M3+CanPYmvRp6BOiW0/riZjWGerRN7+JH4wEF0wJQ==';
  script.crossOrigin = 'anonymous';
  script.dataset.suncoEqualwebScript = 'true';
  script.setAttribute('data-cfasync', 'true');

  document.body.appendChild(script);
}

function getAccessibilitySettings() {
  try {
    return JSON.parse(window.localStorage.getItem(accessibilityStorageKey)) || {};
  } catch (_error) {
    return {};
  }
}

function saveAccessibilitySettings(settings) {
  try {
    window.localStorage.setItem(accessibilityStorageKey, JSON.stringify(settings));
  } catch (_error) {
    // Local storage can be unavailable in restricted browser contexts.
  }
}

function applyAccessibilitySettings(settings, container) {
  Object.entries(accessibilityClasses).forEach(([key, className]) => {
    document.documentElement.classList.toggle(className, Boolean(settings[key]));
  });

  container?.querySelectorAll('[data-sunco-a11y-action]').forEach((button) => {
    const action = button.dataset.suncoA11yAction;
    if (action === 'reset') return;
    button.setAttribute('aria-pressed', settings[action] ? 'true' : 'false');
  });
}

function isEqualWebMenuOpen() {
  return document.body.classList.contains('INDmenu-open')
    && !document.getElementById('INDmenu-btn')?.classList.contains('INDbtn-loading');
}

function initAccessibilityFallback(footer) {
  const trigger = footer.querySelector('.sunco-footer__accessibility');
  const modal = footer.querySelector('[data-sunco-accessibility-modal]');
  if (!trigger || !modal) return;

  const settings = getAccessibilitySettings();
  applyAccessibilitySettings(settings, modal);

  modal.querySelectorAll('[data-sunco-a11y-action]').forEach((button) => {
    const action = button.dataset.suncoA11yAction;
    if (action !== 'reset') {
      button.setAttribute('aria-pressed', settings[action] ? 'true' : 'false');
    }

    button.addEventListener('click', () => {
      if (action === 'reset') {
        Object.keys(settings).forEach((key) => {
          settings[key] = false;
        });
      } else {
        settings[action] = !settings[action];
      }

      applyAccessibilitySettings(settings, modal);
      saveAccessibilitySettings(settings);
    });
  });

  trigger.addEventListener('keydown', (event) => {
    if (event.key !== ' ') return;
    event.preventDefault();
    trigger.click();
  });

  trigger.addEventListener('click', () => {
    window.setTimeout(() => {
      const activationSource = accessibilityTriggerSources.get(trigger);
      accessibilityTriggerSources.delete(trigger);

      if (!isEqualWebMenuOpen()) {
        openModal(modal, activationSource || trigger);
      }
    }, 1200);
  });
}

function initAccessibilitySkipLink(footer) {
  const skipTrigger = document.querySelector('[data-sunco-accessibility-skip]');
  const footerTrigger = footer.querySelector('.sunco-footer__accessibility');
  if (!skipTrigger || !footerTrigger || skipTrigger.dataset.suncoAccessibilityBound === 'true') return;

  skipTrigger.dataset.suncoAccessibilityBound = 'true';
  skipTrigger.addEventListener('click', () => {
    accessibilityTriggerSources.set(footerTrigger, skipTrigger);
    footerTrigger.click();
  });
}

function openHeaderDrawer() {
  const headerDrawer = document.querySelector('header-drawer');
  const details = headerDrawer?.querySelector('#Details-menu-drawer-container, details[ref="details"]');
  const trigger = details?.querySelector('summary') || headerDrawer?.querySelector('summary') || document.querySelector('.header__icon--menu');

  if (trigger instanceof HTMLElement) {
    trigger.click();
    return;
  }

  if (headerDrawer && typeof headerDrawer.open === 'function') {
    if (details instanceof HTMLDetailsElement) details.setAttribute('open', '');
    headerDrawer.open();
  }
}

function openCartDrawer(fallbackUrl) {
  const fallback = () => {
    if (fallbackUrl) window.location.href = fallbackUrl;
  };

  const openExistingDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }

    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger instanceof HTMLElement) {
      trigger.click();
      return true;
    }

    return false;
  };

  if (openExistingDrawer()) return;

  if (window.customElements && typeof window.customElements.whenDefined === 'function') {
    window.customElements
      .whenDefined('cart-drawer-component')
      .then(() => {
        if (!openExistingDrawer()) fallback();
      })
      .catch(fallback);
    return;
  }

  fallback();
}

function syncMobileBottomNavActiveState(nav) {
  if (!(nav instanceof HTMLElement)) return;

  const setActive = (selector, active, usePressed = true, useCurrent = false) => {
    const item = nav.querySelector(selector);
    if (!(item instanceof HTMLElement)) return;

    item.classList.toggle('is-active', active);
    if (usePressed && item.tagName === 'BUTTON') item.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (item.tagName === 'A' && useCurrent) {
      if (active) item.setAttribute('aria-current', 'page');
      else item.removeAttribute('aria-current');
    }
  };

  const menuDetails = document.querySelector('#Details-menu-drawer-container');
  const cartDialog = document.querySelector('[data-testid="cart-drawer-dialog"]');
  const path = window.location.pathname;
  const isCartOpen = cartDialog instanceof HTMLDialogElement && cartDialog.hasAttribute('open');
  const isMenuOpen = menuDetails instanceof HTMLDetailsElement && menuDetails.hasAttribute('open');

  setActive('[data-sunco-mobile-nav-action="home"]', path === '/' && !isMenuOpen && !isCartOpen, false, true);
  setActive('[data-sunco-mobile-nav-action="menu"]', isMenuOpen);
  setActive('[data-sunco-mobile-nav-action="cart"]', isCartOpen || path === '/cart', false);
  setActive('.sunco-mobile-bottom-nav__item[href*="/account"]', path.startsWith('/account'), false, true);
}

function setupMobileDrawerWidgetLayering() {
  if (document.documentElement.dataset.suncoDrawerWidgetLayeringReady === 'true') return;
  document.documentElement.dataset.suncoDrawerWidgetLayeringReady = 'true';

  const touchNavigationQuery = window.matchMedia('(max-width: 989px)');
  const floatingWidgetSelector = [
    '#google-merchantwidget-iframe-wrapper',
    '#ads-agent-host',
    'body > .needsclick',
    'body > [class*="kl-private-reset-css"]',
    '[data-sunco-drawer-layered="true"]',
  ].join(',');
  let isApplyingLayering = false;

  const preserveLayerStyle = (element, property) => {
    const key = property.replace(/-([a-z])/g, (_match, letter) => letter.toUpperCase());
    const valueKey = `suncoOriginal${key[0].toUpperCase()}${key.slice(1)}Set`;
    if (!(element instanceof HTMLElement) || element.dataset[valueKey] === 'true') return;

    element.dataset[valueKey] = 'true';
    element.dataset[`suncoOriginal${key[0].toUpperCase()}${key.slice(1)}`] = element.style.getPropertyValue(property);
    element.dataset[`suncoOriginal${key[0].toUpperCase()}${key.slice(1)}Priority`] = element.style.getPropertyPriority(property);
  };

  const restoreLayerStyle = (element, property) => {
    const key = property.replace(/-([a-z])/g, (_match, letter) => letter.toUpperCase());
    const valueKey = `suncoOriginal${key[0].toUpperCase()}${key.slice(1)}Set`;
    if (!(element instanceof HTMLElement) || element.dataset[valueKey] !== 'true') return;

    const originalValue = element.dataset[`suncoOriginal${key[0].toUpperCase()}${key.slice(1)}`] || '';
    const originalPriority = element.dataset[`suncoOriginal${key[0].toUpperCase()}${key.slice(1)}Priority`] || '';

    if (originalValue) {
      element.style.setProperty(property, originalValue, originalPriority);
    } else {
      element.style.removeProperty(property);
    }
  };

  const menuIsOpen = () => {
    const menuDetails = document.querySelector('#Details-menu-drawer-container');

    return touchNavigationQuery.matches
      && menuDetails instanceof HTMLDetailsElement
      && menuDetails.hasAttribute('open');
  };

  const drawerIsOpen = () => {
    const cartDialog = document.querySelector('[data-testid="cart-drawer-dialog"]');

    return menuIsOpen()
      || (touchNavigationQuery.matches
        && cartDialog instanceof HTMLDialogElement
        && cartDialog.hasAttribute('open'));
  };

  const apply = () => {
    const widgets = document.querySelectorAll(floatingWidgetSelector);
    if (!widgets.length) return;

    isApplyingLayering = true;
    widgets.forEach((widget) => {
      if (!(widget instanceof HTMLElement)) return;

      const shouldLayer = widget.matches('#google-merchantwidget-iframe-wrapper')
        ? drawerIsOpen()
        : menuIsOpen();

      preserveLayerStyle(widget, 'z-index');
      preserveLayerStyle(widget, 'pointer-events');
      preserveLayerStyle(widget, 'opacity');
      preserveLayerStyle(widget, 'visibility');

      if (shouldLayer) {
        widget.style.setProperty('z-index', '30', 'important');
        widget.style.setProperty('pointer-events', 'none', 'important');
        widget.style.setProperty('opacity', '0', 'important');
        widget.style.setProperty('visibility', 'hidden', 'important');
        widget.dataset.suncoDrawerLayered = 'true';
      } else if (widget.dataset.suncoDrawerLayered === 'true') {
        restoreLayerStyle(widget, 'z-index');
        restoreLayerStyle(widget, 'pointer-events');
        restoreLayerStyle(widget, 'opacity');
        restoreLayerStyle(widget, 'visibility');
        delete widget.dataset.suncoDrawerLayered;
      }
    });

    window.requestAnimationFrame(() => {
      isApplyingLayering = false;
    });
  };

  const observeDrawer = (selector) => {
    const element = document.querySelector(selector);
    if (!(element instanceof HTMLElement) || element.dataset.suncoDrawerLayerObserverReady === 'true') return;

    element.dataset.suncoDrawerLayerObserverReady = 'true';
    new MutationObserver(apply).observe(element, { attributes: true, attributeFilter: ['open', 'class'] });
  };

  const observeWidgets = () => {
    document.querySelectorAll(floatingWidgetSelector).forEach((widget) => {
      if (!(widget instanceof HTMLElement) || widget.dataset.suncoDrawerLayerObserverReady === 'true') return;

      widget.dataset.suncoDrawerLayerObserverReady = 'true';
      new MutationObserver(() => {
        if (isApplyingLayering) return;
        window.requestAnimationFrame(apply);
      }).observe(widget, { attributes: true, attributeFilter: ['style', 'class'] });
    });
  };

  const refreshObservers = () => {
    observeDrawer('#Details-menu-drawer-container');
    observeDrawer('[data-testid="cart-drawer-dialog"]');
    observeWidgets();
    apply();
  };

  let refreshFrame = 0;
  const scheduleRefreshObservers = () => {
    if (refreshFrame) return;
    refreshFrame = window.requestAnimationFrame(() => {
      refreshFrame = 0;
      refreshObservers();
    });
  };

  refreshObservers();
  new MutationObserver(scheduleRefreshObservers).observe(document.body, { childList: true });
  touchNavigationQuery.addEventListener('change', apply);
  window.addEventListener('sunco:floating-widgets-update', apply);
  window.setTimeout(refreshObservers, 400);
  window.setTimeout(refreshObservers, 1200);
  window.setTimeout(refreshObservers, 2400);
}

function openLiveChat() {
  const root = document.documentElement;
  const requestWidgetUpdate = () => {
    window.dispatchEvent(new CustomEvent('sunco:floating-widgets-update'));
  };
  const revealHubspot = () => {
    const hubspot = document.querySelector('#hubspot-messages-iframe-container');
    if (!(hubspot instanceof HTMLElement)) return null;

    hubspot.style.removeProperty('opacity');
    hubspot.style.removeProperty('visibility');
    hubspot.style.removeProperty('pointer-events');
    return hubspot;
  };
  const queueOpenWhenReady = () => {
    if (root.dataset.suncoHubspotChatQueued === 'true') return;

    root.dataset.suncoHubspotChatQueued = 'true';
    const onReady = Array.isArray(window.hsConversationsOnReady) ? window.hsConversationsOnReady : [];
    onReady.push(() => {
      delete root.dataset.suncoHubspotChatQueued;
      openLiveChat();
    });
    window.hsConversationsOnReady = onReady;
  };

  root.classList.add('sunco-hubspot-chat-requested');
  window.setTimeout(() => {
    root.classList.remove('sunco-hubspot-chat-requested');
    requestWidgetUpdate();
  }, 5000);

  const hubspotWidget = window.HubSpotConversations?.widget;
  if (hubspotWidget && typeof hubspotWidget.open === 'function') {
    revealHubspot();
    hubspotWidget.open();
    window.setTimeout(requestWidgetUpdate, 100);
    window.setTimeout(requestWidgetUpdate, 700);
    window.setTimeout(requestWidgetUpdate, 1800);
    return;
  }

  const hubspot = revealHubspot();
  if (hubspot instanceof HTMLElement) {
    hubspot.click();
    window.setTimeout(requestWidgetUpdate, 100);
    window.setTimeout(requestWidgetUpdate, 700);
    window.setTimeout(requestWidgetUpdate, 1800);
    return;
  }

  queueOpenWhenReady();
}

window.addEventListener('sunco:open-live-chat', openLiveChat);
document.documentElement.dataset.suncoLiveChatEventReady = 'true';

function setupLiveChatLinks() {
  const root = document.documentElement;
  if (root.dataset.suncoLiveChatLinksReady === 'true') return;
  root.dataset.suncoLiveChatLinksReady = 'true';

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('a[href="#hs-chat-open"]');

    if (!(trigger instanceof HTMLAnchorElement) || trigger.matches('[data-sunco-cart-chat]')) return;

    event.preventDefault();
    openLiveChat();
  });
}

function setupMobileBottomNav() {
  const nav = document.querySelector('[data-sunco-mobile-bottom-nav]');
  if (!(nav instanceof HTMLElement) || nav.classList.contains('sunco-mobile-bottom-nav--product-hidden')) return;

  const mobileNavQuery = window.matchMedia('(max-width: 989px)');
  const revealDistance = 56;
  const hideDistance = 24;
  const gestureResetMs = 180;
  let lastScrollY = Math.max(0, window.scrollY);
  let lastMovementAt = 0;
  let upwardDistance = 0;
  let downwardDistance = 0;
  let ticking = false;

  const setVisible = (visible) => {
    nav.classList.toggle('is-hidden', !visible);
    document.documentElement.classList.toggle('sunco-mobile-bottom-nav-visible', visible);
    window.dispatchEvent(new CustomEvent('sunco:mobile-bottom-nav-toggle', { detail: { visible } }));
  };

  const updateNavHeight = () => {
    document.documentElement.style.setProperty('--sunco-mobile-bottom-nav-height', `${Math.ceil(nav.getBoundingClientRect().height)}px`);
  };

  const handleScroll = () => {
    const currentScrollY = Math.max(0, window.scrollY);
    const delta = currentScrollY - lastScrollY;
    const movementAt = window.performance.now();

    if (!mobileNavQuery.matches) {
      upwardDistance = 0;
      downwardDistance = 0;
      lastMovementAt = 0;
    } else if (currentScrollY < 48) {
      upwardDistance = 0;
      downwardDistance = 0;
      lastMovementAt = 0;
      setVisible(true);
    } else {
      if (delta !== 0 && movementAt - lastMovementAt > gestureResetMs) {
        upwardDistance = 0;
        downwardDistance = 0;
      }

      if (delta !== 0) lastMovementAt = movementAt;

      if (delta < 0) {
        upwardDistance += Math.abs(delta);
        downwardDistance = 0;

        if (upwardDistance >= revealDistance) {
          upwardDistance = 0;
          setVisible(true);
        }
      } else if (delta > 0) {
        downwardDistance += delta;
        upwardDistance = 0;

        if (downwardDistance >= hideDistance) {
          downwardDistance = 0;
          setVisible(false);
        }
      }
    }

    lastScrollY = currentScrollY;
    ticking = false;
  };

  nav.querySelectorAll('[data-sunco-mobile-nav-action]').forEach((button) => {
    button.addEventListener('click', (event) => {
      if (button.dataset.suncoMobileNavAction === 'menu') {
        openHeaderDrawer();
        window.setTimeout(() => syncMobileBottomNavActiveState(nav), 0);
      }
      if (button.dataset.suncoMobileNavAction === 'chat') openLiveChat();
      if (button.dataset.suncoMobileNavAction === 'cart') {
        event.preventDefault();
        openCartDrawer(button instanceof HTMLAnchorElement ? button.href : window.Theme?.routes?.cart_url);
        window.setTimeout(() => syncMobileBottomNavActiveState(nav), 100);
        window.setTimeout(() => syncMobileBottomNavActiveState(nav), 450);
        window.setTimeout(() => syncMobileBottomNavActiveState(nav), 900);
      }
    });
  });

  updateNavHeight();
  setVisible(true);
  window.setTimeout(updateNavHeight, 400);
  window.setTimeout(updateNavHeight, 1200);
  syncMobileBottomNavActiveState(nav);

  const menuDetails = document.querySelector('#Details-menu-drawer-container');
  if (menuDetails instanceof HTMLDetailsElement) {
    new MutationObserver(() => syncMobileBottomNavActiveState(nav)).observe(menuDetails, { attributes: true, attributeFilter: ['open'] });
  }

  const observeCartDrawer = () => {
    const cartDialog = document.querySelector('[data-testid="cart-drawer-dialog"]');
    if (!(cartDialog instanceof HTMLDialogElement) || cartDialog.dataset.suncoBottomNavObserved === 'true') return;

    cartDialog.dataset.suncoBottomNavObserved = 'true';
    new MutationObserver(() => syncMobileBottomNavActiveState(nav)).observe(cartDialog, { attributes: true, attributeFilter: ['open'] });
  };

  let cartDrawerObserverFrame = 0;
  const scheduleObserveCartDrawer = () => {
    if (cartDrawerObserverFrame) return;
    cartDrawerObserverFrame = window.requestAnimationFrame(() => {
      cartDrawerObserverFrame = 0;
      observeCartDrawer();
    });
  };

  observeCartDrawer();
  new MutationObserver(scheduleObserveCartDrawer).observe(document.body, { childList: true });

  window.addEventListener('resize', updateNavHeight);
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(handleScroll);
    },
    { passive: true }
  );
}

function setupMicrosoftAgentMobilePlacement() {
  const root = document.documentElement;
  if (root.dataset.suncoMicrosoftAgentPlacementReady === 'true') return;
  root.dataset.suncoMicrosoftAgentPlacementReady = 'true';

  const mobileNavQuery = window.matchMedia('(max-width: 989px)');
  const safeGap = 10;
  const originalBottomStyles = new WeakMap();
  let observedShadowRoot = null;
  let shadowObserver = null;
  let updateFrame = 0;

  const preserveBottom = (element) => {
    if (!(element instanceof HTMLElement) || originalBottomStyles.has(element)) return;

    const computedBottom = Number.parseFloat(getComputedStyle(element).bottom);
    originalBottomStyles.set(element, {
      value: element.style.getPropertyValue('bottom'),
      priority: element.style.getPropertyPriority('bottom'),
      computed: Number.isFinite(computedBottom) ? computedBottom : 0,
    });
  };

  const restoreBottom = (element) => {
    const original = originalBottomStyles.get(element);
    if (!(element instanceof HTMLElement) || !original) return;

    if (original.value) {
      element.style.setProperty('bottom', original.value, original.priority);
    } else {
      element.style.removeProperty('bottom');
    }
    delete element.dataset.suncoMicrosoftAgentAdjusted;
  };

  const getFixedAgentContainer = (host) => {
    if (!(host instanceof HTMLElement) || !(host.shadowRoot instanceof ShadowRoot)) return null;

    const trigger = host.shadowRoot.querySelector('[role="button"][aria-label*="Shopping Agent"]');
    let container = trigger instanceof HTMLElement ? trigger : null;

    while (container && getComputedStyle(container).position !== 'fixed') {
      container = container.parentElement;
    }

    return container instanceof HTMLElement ? container : null;
  };

  const update = () => {
    updateFrame = 0;

    const host = document.querySelector('#ads-agent-host');
    const agent = getFixedAgentContainer(host);
    if (!(agent instanceof HTMLElement)) return;

    preserveBottom(agent);

    const bottomNav = document.querySelector(
      '[data-sunco-mobile-bottom-nav]:not(.sunco-mobile-bottom-nav--product-hidden)'
    );
    const shouldProtectBottomNav = mobileNavQuery.matches
      && bottomNav instanceof HTMLElement
      && !bottomNav.classList.contains('is-hidden');

    if (!shouldProtectBottomNav) {
      if (agent.dataset.suncoMicrosoftAgentAdjusted === 'true') restoreBottom(agent);
      return;
    }

    const navRect = bottomNav.getBoundingClientRect();
    const agentRect = agent.getBoundingClientRect();
    if (navRect.height <= 0 || agentRect.height <= 0) return;

    const original = originalBottomStyles.get(agent);
    const currentBottom = Number.parseFloat(getComputedStyle(agent).bottom);
    const baseBottom = original?.computed || 0;
    const overlapCorrection = agentRect.bottom - (navRect.top - safeGap);
    const desiredBottom = Math.max(
      baseBottom,
      (Number.isFinite(currentBottom) ? currentBottom : baseBottom) + overlapCorrection
    );

    if (desiredBottom > baseBottom + 0.5) {
      const value = `${Math.ceil(desiredBottom)}px`;
      if (agent.style.getPropertyValue('bottom') !== value || agent.style.getPropertyPriority('bottom') !== 'important') {
        agent.style.setProperty('bottom', value, 'important');
      }
      agent.dataset.suncoMicrosoftAgentAdjusted = 'true';
    } else if (agent.dataset.suncoMicrosoftAgentAdjusted === 'true') {
      restoreBottom(agent);
    }
  };

  const scheduleUpdate = () => {
    if (updateFrame) return;
    updateFrame = window.requestAnimationFrame(update);
  };

  const observeAgent = () => {
    const host = document.querySelector('#ads-agent-host');
    const nextShadowRoot = host instanceof HTMLElement ? host.shadowRoot : null;
    if (!(nextShadowRoot instanceof ShadowRoot) || nextShadowRoot === observedShadowRoot) return;

    shadowObserver?.disconnect();
    observedShadowRoot = nextShadowRoot;
    shadowObserver = new MutationObserver(scheduleUpdate);
    shadowObserver.observe(nextShadowRoot, { childList: true, subtree: true });
    nextShadowRoot.addEventListener('transitionend', scheduleUpdate);
    nextShadowRoot.addEventListener('animationend', scheduleUpdate);
  };

  const refresh = () => {
    observeAgent();
    scheduleUpdate();
  };

  const handleAgentInteraction = (event) => {
    const host = document.querySelector('#ads-agent-host');
    if (!(host instanceof HTMLElement) || !event.composedPath().includes(host)) return;

    scheduleUpdate();
    window.setTimeout(scheduleUpdate, 120);
    window.setTimeout(scheduleUpdate, 500);
  };

  const handleBottomNavToggle = () => {
    scheduleUpdate();
    window.setTimeout(scheduleUpdate, 220);
  };

  new MutationObserver(refresh).observe(document.body, { childList: true });
  document.addEventListener('pointerup', handleAgentInteraction, true);
  document.addEventListener('click', handleAgentInteraction, true);
  mobileNavQuery.addEventListener('change', scheduleUpdate);
  window.addEventListener('resize', scheduleUpdate);
  window.addEventListener('sunco:mobile-bottom-nav-toggle', handleBottomNavToggle);
  window.addEventListener('sunco:floating-widgets-update', scheduleUpdate);

  refresh();
  window.setTimeout(refresh, 400);
  window.setTimeout(refresh, 1200);
  window.setTimeout(refresh, 2400);
  window.setTimeout(refresh, 5000);
}

function setupGoogleMerchantInitialRevealGuard() {
  if (document.documentElement.dataset.suncoGoogleMerchantRevealGuardReady === 'true') return;
  document.documentElement.dataset.suncoGoogleMerchantRevealGuardReady = 'true';

  const observedElements = new WeakSet();
  const resizeObserver = new ResizeObserver(() => window.requestAnimationFrame(checkReadiness));

  const hasCompactBadgeSize = (element) => {
    if (!(element instanceof HTMLElement)) return false;

    const rect = element.getBoundingClientRect();
    return rect.width >= 40 && rect.width <= 500 && rect.height >= 40 && rect.height <= 200;
  };

  const observeSize = (element) => {
    if (!(element instanceof HTMLElement) || observedElements.has(element)) return;

    observedElements.add(element);
    resizeObserver.observe(element);
  };

  function checkReadiness() {
    const wrapper = document.querySelector('#google-merchantwidget-iframe-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const iframe = wrapper.querySelector('#merchantwidgetiframe');
    observeSize(wrapper);

    if (wrapper.dataset.suncoWidgetInitialReady === 'true') return;
    if (!(iframe instanceof HTMLIFrameElement) || !hasCompactBadgeSize(wrapper)) return;

    wrapper.dataset.suncoWidgetInitialReady = 'true';
    window.dispatchEvent(new CustomEvent('sunco:floating-widgets-update'));
  }

  new MutationObserver(checkReadiness).observe(document.body, { childList: true, subtree: true });
  checkReadiness();
}

function setupFloatingWidgetPlacement() {
  if (document.querySelector('main[data-template^="product"]')) return;
  if (document.documentElement.dataset.suncoFloatingWidgetPlacementReady === 'true') return;
  document.documentElement.dataset.suncoFloatingWidgetPlacementReady = 'true';

  const googleLeftOffset = '0px';
  const mobileNavQuery = window.matchMedia('(max-width: 989px)');
  const root = document.documentElement;
  let isApplyingWidgetPosition = false;

  const applyStyle = (element, property, value) => {
    if (!(element instanceof HTMLElement)) return;

    if (element.style.getPropertyValue(property) !== value || element.style.getPropertyPriority(property) !== 'important') {
      element.style.setProperty(property, value, 'important');
    }
  };

  const markPositioned = (element) => {
    if (element instanceof HTMLElement) element.dataset.suncoWidgetPositioned = 'true';
  };

  const setHubspotLauncherVisibility = (hubspot, hidden) => {
    if (!(hubspot instanceof HTMLElement)) return;

    if (hidden) {
      applyStyle(hubspot, 'opacity', '0');
      applyStyle(hubspot, 'visibility', 'hidden');
      applyStyle(hubspot, 'pointer-events', 'none');
      return;
    }

    hubspot.style.removeProperty('opacity');
    hubspot.style.removeProperty('visibility');
    hubspot.style.removeProperty('pointer-events');
  };

  const update = () => {
    const google = document.querySelector('#google-merchantwidget-iframe-wrapper');
    const hubspot = document.querySelector('#hubspot-messages-iframe-container');
    if (!(google instanceof HTMLElement) && !(hubspot instanceof HTMLElement)) return;

    const bottomNav = document.querySelector('[data-sunco-mobile-bottom-nav]:not(.sunco-mobile-bottom-nav--product-hidden)');
    const isBottomNavVisible = mobileNavQuery.matches
      && bottomNav instanceof HTMLElement
      && !bottomNav.classList.contains('is-hidden');
    const bottomOffset = isBottomNavVisible ? `${Math.ceil(bottomNav.getBoundingClientRect().height + 10)}px` : '0px';
    const hubspotMaxHeight = mobileNavQuery.matches ? `calc(100dvh - ${bottomOffset} - 20px)` : '';

    isApplyingWidgetPosition = true;
    root.style.setProperty('--sunco-floating-widget-bottom-offset', bottomOffset);

    if (hubspot instanceof HTMLElement) {
      const hubspotRect = hubspot.getBoundingClientRect();
      const isExpandedChat = hubspotRect.width > 180 && hubspotRect.height > 180;
      const isChatOpening = root.classList.contains('sunco-hubspot-chat-requested');

      applyStyle(hubspot, 'bottom', bottomOffset);
      if (hubspotMaxHeight) {
        applyStyle(hubspot, 'max-height', hubspotMaxHeight);
        applyStyle(hubspot, 'overflow', 'hidden');
      } else {
        hubspot.style.removeProperty('max-height');
        hubspot.style.removeProperty('overflow');
      }
      setHubspotLauncherVisibility(hubspot, !isExpandedChat && !isChatOpening);
      markPositioned(hubspot);
    }

    if (google instanceof HTMLElement) {
      applyStyle(google, 'left', googleLeftOffset);
      if (hubspot instanceof HTMLElement) {
        applyStyle(google, 'bottom', getComputedStyle(hubspot).bottom || bottomOffset);
      } else {
        applyStyle(google, 'bottom', bottomOffset);
      }
      markPositioned(google);
    }

    isApplyingWidgetPosition = false;
  };

  const observeGoogle = () => {
    const google = document.querySelector('#google-merchantwidget-iframe-wrapper');
    if (!(google instanceof HTMLElement) || google.dataset.suncoStyleObserverReady === 'true') return;

    google.dataset.suncoStyleObserverReady = 'true';
    const observer = new MutationObserver(() => {
      if (isApplyingWidgetPosition) return;
      window.requestAnimationFrame(update);
    });
    observer.observe(google, { attributes: true, attributeFilter: ['style'] });
  };

  const observeHubspot = () => {
    const hubspot = document.querySelector('#hubspot-messages-iframe-container');
    if (!(hubspot instanceof HTMLElement) || hubspot.dataset.suncoStyleObserverReady === 'true') return;

    hubspot.dataset.suncoStyleObserverReady = 'true';
    const observer = new MutationObserver(() => {
      if (isApplyingWidgetPosition) return;
      window.requestAnimationFrame(update);
    });
    observer.observe(hubspot, { attributes: true, attributeFilter: ['style', 'class'] });
  };

  const observeBody = () => {
    let bodyFrame = 0;
    const observer = new MutationObserver(() => {
      if (bodyFrame) return;
      bodyFrame = window.requestAnimationFrame(() => {
        bodyFrame = 0;
        observeGoogle();
        observeHubspot();
        update();
      });
    });
    observer.observe(document.body, { childList: true });
  };

  observeGoogle();
  observeHubspot();
  observeBody();
  update();

  window.addEventListener('resize', update);
  window.addEventListener('sunco:mobile-bottom-nav-toggle', update);
  window.addEventListener('sunco:floating-widgets-update', update);
  window.setTimeout(update, 400);
  window.setTimeout(update, 1200);
  window.setTimeout(update, 2400);
}

document.querySelectorAll('[data-sunco-footer]').forEach((footer) => {
  initFooterAccordions(footer);
  initFooterModals(footer);
  initEqualWeb(footer);
  initAccessibilityFallback(footer);
  initAccessibilitySkipLink(footer);
});

setupMobileBottomNav();
setupMicrosoftAgentMobilePlacement();
setupGoogleMerchantInitialRevealGuard();
setupLiveChatLinks();
setupMobileDrawerWidgetLayering();
setupFloatingWidgetPlacement();
